/*

<article b-tpl="slider" data-moonwalk-run="loop">
  <div class="inner">
    <div class="heading">
      {% ref refs.heading %}
    </div>
    {% datasource %}
    <div data-looper-container>
      <section class="items" data-looper data-looper-reverse>
        {% for entry in entries %}
          <div data-panner-item>
            {% picture entry.image { sizes: 'auto', placeholder: 'dominant_color_faded', lazyload: true, srcset: 'Ytri.Slider.SliderItem:image.default', prefix: '/media' } %}
          </div>
        {% endfor %}
      </section>
    </div>
    {% enddatasource %}
  </div>
</article>



Moonwalk:

runs: {
  loop: {
    threshold: 0.0,
    repeated: true,
    callback: (element, repeated) => {
      if (repeated) {
        element.$loop.play()
        return
      }
    },

    onExit: (element, repeated) => {
      element.$loop.pause()
    },
  },
},

Initialization:



*/

import { gsap, Dom, Draggable, _defaultsDeep, InertiaPlugin } from '@brandocms/jupiter'

gsap.registerPlugin(Draggable)
gsap.registerPlugin(InertiaPlugin)

/*
This helper function makes a group of elements animate along the x-axis in a seamless, responsive loop.

Features:
 - Uses xPercent so that even if the widths change (like if the window gets resized), it should still work in most cases.
 - When each item animates to the left or right enough, it will loop back to the other side
 - Optionally pass in a config object with values like draggable: true, center: true, speed (default: 1, which travels at roughly 100 pixels per second), paused (boolean), repeat, reversed, and paddingRight.
 - The returned timeline will have the following methods added to it:
   - next() - animates to the next element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
   - previous() - animates to the previous element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
   - toIndex() - pass in a zero-based index value of the element that it should animate to, and optionally pass in a vars object to control duration, easing, etc. Always goes in the shortest direction
   - current() - returns the current index (if an animation is in-progress, it reflects the final index)
   - times - an Array of the times on the timeline where each element hits the "starting" spot.

HTML Structure for proper viewport behavior:
 - Base container: Apply @space container padding for content alignment
 - [data-looper-container]: Apply @space margin-x -container and overflow-x: clip to extend into container padding
 - [data-looper]: Apply @space padding-x container to position items correctly within the grid
 
This structure allows items to:
 - Calculate widths correctly based on the container grid
 - Remain visible while scrolling through the container padding area
 - Disappear only when truly outside the viewport
 */
export function horizontalLoop(app, items, config) {
  items = gsap.utils.toArray(items)
  config = config || {}
  const shouldLoop = config.loop !== false // Default to true for backward compatibility

  // The container is either the parentNode or a specific element in config.center
  let center = config.center,
    container =
      center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode

  /**
   * Measures the total width of all the items as they're currently laid out.
   */
  function getTotalWidthOfItems() {
    if (!items.length) return 0

    let startX = items[0].offsetLeft,
      last = items[items.length - 1],
      lastXPercent = (gsap.getProperty(last, 'xPercent') || 0) / 100,
      lastWidth = parseFloat(gsap.getProperty(last, 'width', 'px')),
      lastScale = gsap.getProperty(last, 'scaleX') || 1,
      totalWidth =
        last.offsetLeft +
        lastXPercent * lastWidth -
        startX +
        last.offsetWidth * lastScale +
        (parseFloat(config.paddingRight) || 0)

    return totalWidth
  }

  /**
   * Replicates items until total width >= container width.
   * Mark clones as aria-hidden.
   */
  function replicateItemsIfNeeded() {
    // Only replicate items if we're in loop mode
    if (!shouldLoop) return

    let totalWidth = getTotalWidthOfItems()

    // Calculate minimum width needed for seamless looping
    const containerWidth = container.offsetWidth

    // Safety check: If items have no layout yet (hidden/not rendered), bail out
    // This prevents exponential duplication when offsetWidth returns 0
    if (containerWidth === 0 || totalWidth === 0) {
      return
    }

    // Find the width of the first and last items (these appear when changing direction)
    const firstItemWidth = items[0].offsetWidth
    const lastItemWidth = items[items.length - 1].offsetWidth

    // We need container width + the larger of first/last item (worst case for direction changes)
    const minRequiredWidth = containerWidth + Math.max(firstItemWidth, lastItemWidth)

    // Safety cap so we don't accidentally infinite-loop
    // if there's some unexpected layout scenario.
    let maxReplication = 10
    let count = 0
    const originalItemCount = items.length
    let previousTotalWidth = totalWidth

    // Replicate items until we have enough content for seamless looping
    while (totalWidth < minRequiredWidth && count < maxReplication) {
      // Clone ONLY the original items, not the clones we've already made
      // This prevents exponential growth
      for (let i = 0; i < originalItemCount; i++) {
        const clone = items[i].cloneNode(true)
        // clone.setAttribute('aria-hidden', 'true')
        container.appendChild(clone)
        items.push(clone)
      }

      // Force browser to recalculate layout before measuring
      container.getBoundingClientRect()

      totalWidth = getTotalWidthOfItems()
      count++

      // Safety check: if width isn't increasing, something is wrong - bail out
      if (totalWidth <= previousTotalWidth && count > 1) {
        break
      }

      previousTotalWidth = totalWidth
    }
  }

  replicateItemsIfNeeded()

  let onChange = config.onChange,
    lastIndex = 0,
    tl = gsap.timeline({
      repeat: shouldLoop ? config.repeat : 0, // Only repeat if looping
      onUpdate:
        onChange &&
        function () {
          let i = tl.closestIndex()
          if (lastIndex !== i) {
            lastIndex = i
            onChange(items[i], i)
          }
        },
      paused: config.paused,
      defaults: { ease: 'none' },
      onReverseComplete: () => shouldLoop && tl.totalTime(tl.rawTime() + tl.duration() * 100),
    }),
    length = items.length,
    startX = items[0].offsetLeft,
    times = [],
    widths = [],
    spaceBefore = [],
    xPercents = [],
    curIndex = 0,
    indexIsDirty = false,
    pixelsPerSecond = (config.speed || 1) * 100,
    snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1),
    timeOffset = 0,
    totalWidth,
    // We'll reuse this container reference from above
    // container = ...
    centerIsBoolean = center === true,
    getTotalWidth = () =>
      items[length - 1].offsetLeft +
      (xPercents[length - 1] / 100) * widths[length - 1] -
      startX +
      spaceBefore[0] +
      items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') +
      (parseFloat(config.paddingRight) || 0),
    timeWrap,
    populateWidths = () => {
      let b1 = container.getBoundingClientRect(),
        b2
      items.forEach((el, i) => {
        widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px'))
        xPercents[i] = snap(
          (parseFloat(gsap.getProperty(el, 'x', 'px')) / widths[i]) * 100 +
            gsap.getProperty(el, 'xPercent')
        )
        b2 = el.getBoundingClientRect()
        spaceBefore[i] = b2.left - (i ? b1.right : b1.left)
        b1 = b2
      })
      gsap.set(items, {
        // convert "x" to "xPercent" to make things responsive
        xPercent: i => xPercents[i],
      })
      totalWidth = getTotalWidth()
    },
    populateOffsets = () => {
      timeOffset = centerIsBoolean
        ? (tl.duration() * (container.offsetWidth / 2)) / totalWidth
        : 0
      if (centerIsBoolean) {
        times.forEach((t, i) => {
          times[i] = timeWrap(
            tl.labels['label' + i] + (tl.duration() * widths[i]) / 2 / totalWidth - timeOffset
          )
        })
      }
    },
    getClosest = (values, value, wrap) => {
      let i = values.length,
        closest = 1e10,
        index = 0,
        d
      while (i--) {
        d = Math.abs(values[i] - value)
        if (d > wrap / 2) {
          d = wrap - d
        }
        if (d < closest) {
          closest = d
          index = i
        }
      }
      return index
    },
    populateTimeline = () => {
      let i, item, curX, distanceToStart, distanceToLoop
      tl.clear()

      // Update startX on each timeline population to handle resize
      startX = items[0].offsetLeft

      if (shouldLoop) {
        // Looping behavior - add animations for seamless looping
        for (i = 0; i < length; i++) {
          item = items[i]
          curX = (xPercents[i] / 100) * widths[i]
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0]
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX')
          tl.to(
            item,
            {
              xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
              duration: distanceToLoop / pixelsPerSecond,
            },
            0
          )
            .fromTo(
              item,
              {
                xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100),
              },
              {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                immediateRender: false,
              },
              distanceToLoop / pixelsPerSecond
            )
            .add('label' + i, distanceToStart / pixelsPerSecond)
          times[i] = distanceToStart / pixelsPerSecond
        }

        // After all times are calculated, adjust them for container padding
        const itemsContainer = items[0].parentNode
        const containerPaddingLeft = parseFloat(getComputedStyle(itemsContainer).paddingLeft) || 0
        if (containerPaddingLeft > 0) {
          // Convert padding to timeline time and adjust all snap times
          // Subtract padding time because items appear further right due to padding
          const paddingTime = containerPaddingLeft / pixelsPerSecond
          times = times.map(time => time - paddingTime)
        }
      } else {
        // Non-looping behavior - create a simple linear animation
        // Calculate total distance all items can travel
        let totalDistance = totalWidth - container.offsetWidth
        if (totalDistance <= 0) {
          // If content fits within container, no animation needed
          return
        }

        // Create a single animation that moves all items together
        let duration = totalDistance / pixelsPerSecond

        // Starting position (0%)
        tl.addLabel('start', 0)

        // Add animation for each item
        for (i = 0; i < length; i++) {
          item = items[i]

          // Calculate how far this item needs to move
          let startPercent = xPercents[i]
          let endPercent = startPercent - (totalDistance / widths[i]) * 100

          tl.to(
            item,
            {
              xPercent: endPercent,
              duration: duration,
            },
            0
          )

          // Add label for each item's position
          let itemPosition = i / (length - 1)
          tl.addLabel('label' + i, itemPosition * duration)
          times[i] = itemPosition * duration
        }

        // End position (100%)
        tl.addLabel('end', duration)
      }

      timeWrap = gsap.utils.wrap(0, tl.duration())

      // If draggable exists, update it after timeline changes
      if (tl.draggable) {
        tl.draggable.update(true)
      }
    },
    refresh = deep => {
      let progress = tl.progress()
      tl.progress(0, true)
      populateWidths()
      deep && populateTimeline()
      populateOffsets()
      deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true)
    },
    proxy

  gsap.set(items, { x: 0 })
  populateWidths()
  populateTimeline()
  populateOffsets()

  // Listen for APPLICATION:RESIZE to re-calculate
  window.addEventListener('APPLICATION:RESIZE', e => {
    if (!e.detail.widthChanged) {
      return
    }
    refresh(true)
  })

  function toIndex(index, vars) {
    vars = vars || {}

    if (shouldLoop) {
      // For looping behavior, use shortest path
      Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length) // always go in the shortest direction
    } else {
      // For non-looping, clamp to valid indices
      index = Math.max(0, Math.min(index, length - 1))
    }

    let newIndex = shouldLoop ? gsap.utils.wrap(0, length, index) : index,
      time = times[newIndex]

    if (shouldLoop && time > tl.time() !== index > curIndex && index !== curIndex) {
      // if we're wrapping the timeline's playhead, make the proper adjustments
      time += tl.duration() * (index > curIndex ? 1 : -1)
    }

    if (shouldLoop && (time < 0 || time > tl.duration())) {
      vars.modifiers = { time: timeWrap }
    }

    curIndex = newIndex
    vars.overwrite = true
    gsap.killTweensOf(proxy)
    return vars.duration === 0
      ? tl.time(shouldLoop ? timeWrap(time) : time)
      : tl.tweenTo(time, vars)
  }

  tl.toIndex = (index, vars) => toIndex(index, vars)
  tl.closestIndex = setCurrent => {
    let index = getClosest(times, tl.time(), tl.duration())
    if (setCurrent) {
      curIndex = index
      indexIsDirty = false
    }
    return index
  }
  tl.refreshLoop = refresh
  tl.current = () => (indexIsDirty ? tl.closestIndex(true) : curIndex)
  tl.next = vars => toIndex(tl.current() + 1, vars)
  tl.previous = vars => toIndex(tl.current() - 1, vars)
  tl.times = times
  tl.isReversed = config.reversed
  tl.isLooping = shouldLoop

  // Override play method to handle reversed state with timeScale
  const originalPlay = tl.play
  tl.play = function (...args) {
    // First, call the original play method
    const result = originalPlay.apply(this, args)

    // AFTER play, set the appropriate timeScale for direction
    if (this.isReversed) {
      // Always use negative timeScale for reversed loopers
      tl.timeScale(-1)
    } else {
      // Always use positive timeScale for normal loopers
      tl.timeScale(1)
    }

    return result
  }

  // Set up the isReversed flag
  tl.isReversed = !!config.reversed

  // Draggable logic
  if (config.draggable && typeof Draggable === 'function') {
    proxy = document.createElement('div')
    let wrap = gsap.utils.wrap(0, 1),
      ratio,
      startProgress,
      draggable,
      lastSnap,
      initChangeX,
      align = () => {
        return tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio))
      },
      syncIndex = () => tl.closestIndex(true)

    if (typeof InertiaPlugin === 'undefined') {
      console.warn('InertiaPlugin required for momentum-based scrolling and snapping.')
    }

    // Build draggable config
    const draggableConfig = {
      trigger: container,
      type: 'x',
      zIndexBoost: false,
      allowContextMenu: true,
      onPressInit() {
        let x = this.x
        gsap.killTweensOf(tl)
        // Also kill any ramp-up timeScale animations
        gsap.killTweensOf(tl, 'timeScale')
        startProgress = tl.progress()
        refresh()
        ratio = 1 / totalWidth
        initChangeX = startProgress / -ratio - x

        if (initChangeX > 1000 || initChangeX < -1000) {
          return
        }

        gsap.set(proxy, { x: startProgress / -ratio })
      },
      onDrag: align,
      onThrowUpdate: align,
      overshootTolerance: 0,
      inertia: true,
      onDragStart() {
        tl.pause()
      },
      onRelease() {
        syncIndex()
        draggable.isThrowing && (indexIsDirty = true)
      },
      onThrowComplete() {
        syncIndex()

        if (!shouldLoop) {
          // For non-looping mode, just make sure we're at the right position
          tl.progress(tl.progress())
          return
        }

        // Only resume playing if crawl is enabled
        if (config.crawl) {
          if (tl.isReversed) {
            // For reversed loopers
            tl.progress(tl.progress()) // Lock in current position
            tl.pause()
            tl.play() // Resume
            gsap.fromTo(tl, { timeScale: 0 }, { timeScale: -1, ease: 'power2.in', duration: 2 })
          } else {
            // For normal loopers
            tl.progress(tl.progress()) // Lock in current position
            tl.pause()
            tl.play() // Resume
            gsap.fromTo(tl, { timeScale: 0 }, { timeScale: 1, ease: 'power2.in', duration: 2 })
          }
        }
      },
    }

    // Only add snap function if config.snap is not explicitly false
    if (config.snap !== false) {
      draggableConfig.snap = function(value) {
        // If the user presses & releases mid-throw, we avoid a huge velocity
        if (Math.abs(startProgress / -ratio - this.x) < 10) {
          return lastSnap + initChangeX
        }

        let time = -(value * ratio) * tl.duration(),
          wrappedTime = shouldLoop ? timeWrap(time) : Math.max(0, Math.min(tl.duration(), time)),
          snapTime = times[getClosest(times, shouldLoop ? wrappedTime : time, tl.duration())],
          dif = snapTime - (shouldLoop ? wrappedTime : time)

        if (shouldLoop && Math.abs(dif) > tl.duration() / 2) {
          dif += dif < 0 ? tl.duration() : -tl.duration()
        }

        lastSnap = (time + dif) / tl.duration() / -ratio

        return lastSnap
      }
    }

    draggable = Draggable.create(proxy, draggableConfig)[0]
    tl.draggable = draggable
  }

  tl.closestIndex(true)
  lastIndex = curIndex
  onChange && onChange(items[curIndex], curIndex)

  return tl
}

const DEFAULT_OPTIONS = {
  center: false,
  snap: false, // Set to false to disable snap-to-item behavior when dragging/throwing. Set to true or a number for snapping.
  crawl: true,
  loop: true, // New option to control looping behavior
  speed: {
    sm: 0.1,
    lg: 0.35,
  },

  ease: {
    mouseOver: { timeScale: 0.3, duration: 0.75 },
    mouseOut: { timeScale: 1, duration: 0.75 },
  },

  selector: '[data-moonwalk-run="loop"]',
}

export default class Looper {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = { ..._defaultsDeep(opts, DEFAULT_OPTIONS) }
    this.loopers = []
    this.pendingLoopers = []
    this.init()
  }

  init() {
    this.looperElements = Dom.all(this.opts.selector)
    this.looperElements.forEach(element => {
      // add data-panner-item for backwards compatibility
      const items = Dom.all(element, '[data-panner-item], [data-looper-item]')
      const speed = ['mobile', 'iphone'].includes(this.app.breakpoint)
        ? this.opts.speed.sm
        : this.opts.speed.lg
      const isReverse = element.querySelector('[data-looper-reverse]') !== null
      const hasSnapAttribute = element.querySelector('[data-looper]')?.hasAttribute('data-looper-snap') || false
      // Snap if either config says to snap OR element has data-looper-snap attribute
      const shouldSnap = this.opts.snap || hasSnapAttribute

      // Create stub timeline for Moonwalk compatibility
      // The real timeline will be created in APPLICATION_REVEALED when layout is ready
      const stubLoop = {
        play: () => {},
        pause: () => {},
        isReversed: isReverse,
      }

      element.$loop = stubLoop

      // Store configuration for later initialization
      this.pendingLoopers.push({
        element,
        items,
        config: {
          paused: true,
          repeat: -1,
          draggable: true,
          center: this.opts.center,
          snap: shouldSnap,
          speed,
          reversed: isReverse,
          loop: this.opts.loop,
          crawl: this.opts.crawl,
        }
      })
    })

    // Register callback to finalize loopers when layout is ready
    this.app.registerCallback('APPLICATION:REVEALED', () => {
      this.finalizeLoopers()
    })
  }

  finalizeLoopers() {
    this.pendingLoopers.forEach(({ element, items, config }) => {
      // Now create the real loop with proper layout measurements
      const loop = horizontalLoop(this.app, items, config)

      // Initialize position
      loop.toIndex(0, { duration: 0 })

      // Start playing if crawl is enabled
      if (config.crawl) {
        if (config.reversed) {
          // For reversed loopers, set correct initial position and direction
          loop.progress(1) // Set to end of timeline
          loop.play() // Start playing
          // Set timeScale AFTER play is called to ensure it doesn't get reset
          loop.timeScale(-1)
        } else {
          // For normal loopers
          loop.progress(0) // Set to beginning of timeline
          loop.play() // Start playing
          // Set timeScale AFTER play is called to ensure it doesn't get reset
          loop.timeScale(1)
        }
      }

      // Replace stub with real timeline
      element.$loop = loop

      const next = Dom.find(element, '[data-panner-next]')
      const previous = Dom.find(element, '[data-panner-previous]')

      if (next) {
        next.addEventListener('click', _e => {
          loop.next({ duration: 0.85, ease: 'power2.inOut' })
        })
      }

      if (previous) {
        previous.addEventListener('click', _e => {
          loop.previous({ duration: 0.85, ease: 'power2.inOut' })
        })
      }

      items.forEach(item => {
        item.addEventListener('mouseover', () => {
          gsap.killTweensOf(loop, 'timeScale')
          gsap.to(loop, {
            ...this.opts.ease.mouseOver,
            timeScale: loop.isReversed ? -0.3 : 0.3,
          })
        })

        item.addEventListener('mouseout', () => {
          gsap.killTweensOf(loop, 'timeScale')
          gsap.to(loop, {
            ...this.opts.ease.mouseOut,
            timeScale: loop.isReversed ? -1 : 1,
          })
        })
      })

      gsap.to(element, { opacity: 1, ease: 'none', delay: 0.5 })

      this.loopers.push(loop)
    })

    // Clear pending loopers after initialization
    this.pendingLoopers = []
  }
}
